import { useEffect, useState, useCallback } from "react";
import { ShoppingCart, Plus, Minus, Trash2, Loader2, Receipt, Search, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Simulador de Punto de Venta: arma ticket, aplica promos activas y cobra
// (descuenta inventario). Alimenta el pronóstico de demanda.
export default function RetailPOS() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [quote, setQuote] = useState(null);
  const [cliente, setCliente] = useState("");
  const [busy, setBusy] = useState(false);
  const [sales, setSales] = useState({ sales: [], kpis: {} });

  const loadSales = useCallback(() => { api.get("/retail/pos/sales").then((r) => setSales(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/retail/products").then((r) => setProducts(r.data)).catch(() => {}); loadSales(); }, [loadSales]);

  const inc = (p) => setCart((c) => c.find((x) => x.product_id === p.id) ? c.map((x) => x.product_id === p.id ? { ...x, qty: x.qty + 1 } : x) : [...c, { product_id: p.id, sku: p.sku, nombre: p.descripcion, precio: p.precio, qty: 1 }]);
  const dec = (id) => setCart((c) => c.flatMap((x) => x.product_id === id ? (x.qty > 1 ? [{ ...x, qty: x.qty - 1 }] : []) : [x]));
  const rm = (id) => setCart((c) => c.filter((x) => x.product_id !== id));

  useEffect(() => {
    if (!cart.length) { setQuote(null); return; }
    const body = { items: cart.map((c) => ({ product_id: c.product_id, qty: c.qty })) };
    const t = setTimeout(() => {
      api.post("/retail/pos/quote", body).then((r) => setQuote(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [cart]);

  const checkout = async () => {
    if (!cart.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/retail/pos/checkout", { items: cart.map((c) => ({ product_id: c.product_id, qty: c.qty })), cliente: cliente || undefined });
      toast.success(`Venta ${data.folio} cobrada · ${money(data.total)}`);
      setCart([]); setCliente(""); setQuote(null);
      api.get("/retail/products").then((r) => setProducts(r.data)).catch(() => {});
      loadSales();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };

  const filtered = products.filter((p) => !q.trim() || `${p.sku} ${p.descripcion} ${p.marca}`.toLowerCase().includes(q.toLowerCase()));
  const k = sales.kpis || {};

  return (
    <div data-testid="retail-pos" className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
      {/* Catálogo */}
      <div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[["Venta hoy", money(k.hoy)], ["Tickets", k.tickets || 0], ["Ticket prom.", money(k.ticket_promedio)], ["Piezas", k.piezas || 0]].map(([l, v]) => (
            <div key={l} className="card-surface p-3"><div className="text-[11px] text-navy/40">{l}</div><div className="text-lg font-medium">{v}</div></div>
          ))}
        </div>
        <div className="relative mb-3">
          <Search className="w-4 h-4 text-navy/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto por SKU, nombre o marca…" className="w-full field pl-10" data-testid="pos-search" />
        </div>
        {products.length === 0 ? (
          <div className="card-surface p-8 text-center text-navy/40 text-sm">No hay productos. Crea SKUs en <b>Productos & SKUs</b> para vender aquí.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[60vh] overflow-y-auto thin-scroll pr-1">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => inc(p)} data-testid={`pos-product-${p.sku}`}
                className="text-left card-surface p-3 hover:border-violet/50 transition-colors disabled:opacity-50" disabled={p.inventario <= 0}>
                <div className="text-sm font-medium truncate">{p.descripcion}</div>
                <div className="text-[11px] text-navy/40 truncate">{p.sku}{p.marca ? ` · ${p.marca}` : ""}</div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-violet font-medium">{money(p.precio)}</span>
                  <span className={`text-[11px] ${p.inventario <= 0 ? "text-destructive" : "text-navy/40"}`}>{p.inventario <= 0 ? "Sin stock" : `${p.inventario} disp.`}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Ticket */}
      <div className="card-surface p-5 flex flex-col" data-testid="pos-ticket">
        <div className="flex items-center gap-2 mb-4 text-navy/70"><ShoppingCart className="w-4 h-4" /><span className="font-medium">Ticket</span></div>
        {cart.length === 0 ? (
          <div className="text-sm text-navy/40 py-8 text-center flex-1">Toca un producto para agregarlo.</div>
        ) : (
          <div className="space-y-2 flex-1 overflow-y-auto thin-scroll mb-3">
            {cart.map((c) => {
              const ql = quote?.lines?.find((l) => l.sku === c.sku);
              return (
                <div key={c.product_id} className="flex items-center gap-2" data-testid={`pos-cart-${c.sku}`}>
                  <div className="min-w-0 flex-1"><div className="text-sm truncate">{c.nombre}</div>
                    <div className="text-[11px] text-navy/40">{money(c.precio)}{ql?.promo ? <span className="text-emerald-600 ml-1 inline-flex items-center gap-0.5"><Tag className="w-3 h-3" />{ql.promo}</span> : ""}</div></div>
                  <button onClick={() => dec(c.product_id)} className="w-6 h-6 rounded-full border border-navy/15 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                  <span className="w-6 text-center text-sm">{c.qty}</span>
                  <button onClick={() => inc({ id: c.product_id, sku: c.sku, descripcion: c.nombre, precio: c.precio })} className="w-6 h-6 rounded-full border border-navy/15 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                  <button onClick={() => rm(c.product_id)} className="text-navy/30 hover:text-destructive ml-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        )}

        {quote && (
          <div className="border-t border-navy/10 pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-navy/60"><span>Subtotal</span><span>{money(quote.subtotal)}</span></div>
            {quote.descuento > 0 && <div className="flex justify-between text-emerald-600"><span>Descuento promos</span><span>−{money(quote.descuento)}</span></div>}
            <div className="flex justify-between text-navy/60"><span>IVA</span><span>{money(quote.iva)}</span></div>
            <div className="flex justify-between font-medium text-lg"><span>Total</span><span data-testid="pos-total">{money(quote.total)}</span></div>
          </div>
        )}

        <input value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Cliente (opcional)" className="field-sm mt-3" data-testid="pos-cliente" />
        <button onClick={checkout} disabled={busy || !cart.length} className="btn btn-primary mt-3 w-full" data-testid="pos-checkout">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />} Cobrar
        </button>

        {sales.sales?.length > 0 && (
          <div className="mt-4">
            <div className="overline text-navy/40 mb-1.5">Últimas ventas</div>
            <div className="space-y-1 max-h-40 overflow-y-auto thin-scroll">
              {sales.sales.slice(0, 8).map((s) => (
                <div key={s.id} className="flex justify-between text-xs text-navy/60 border-b border-navy/5 py-1" data-testid={`pos-sale-${s.folio}`}>
                  <span>{s.folio} · {s.cliente}</span><span className="font-medium">{money(s.total)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
